import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Paper,
  Fade,
  Backdrop,
  CircularProgress,
  Fab,
  Zoom,
  useTheme,
  alpha
} from "@mui/material";
import { styled } from "@mui/system";
import CheckIcon from "@mui/icons-material/Check";
import { generateSignedUrl } from "../../utils/getPreSignedURL";
// import { editUser, getCurrentUser } from "../redux/actions/userActions"; // Assuming these actions exist
import { useDispatch } from "react-redux";
import "./CoachAgreement.css";
import useSanitizeContent from "../../utils/useSanitizeContent";
// Helper function to validate URLs (preserved from original code)
const isValidUrl = (string) => {
  try {
    new URL(string);
    return true;
  } catch (_) {
    return false;
  }
};

// Custom styled components for enhanced visual appeal
const StyledModal = styled(Modal)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
}));

const ModalContent = styled(Paper)(({ theme }) => ({
  position: "relative",
  width: "90%",
  maxHeight: "85vh",
  overflow: "auto",
  "::-webkit-scrollbar": {
    width: "5px",
    color: "red"
  },
  "::-webkit-scrollbar-track": {
    background: "#f1f1f1"
  },
  "::-webkit-scrollbar-thumb": {
    background: "#1976d2",
    borderRadius: "10px"
  },
  maxWidth: 800,
  padding: 0,
  borderRadius: theme.spacing(3),
  boxShadow: `0 12px 24px ${alpha(theme.palette.common.black, 0.2)}`,
  "&::-webkit-scrollbar": {
    width: "6px"
  },
  "&::-webkit-scrollbar-track": {
    background: alpha(theme.palette.primary.main, 0.05)
  },
  "&::-webkit-scrollbar-thumb": {
    background: alpha(theme.palette.primary.main, 0.6),
    borderRadius: "6px"
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: theme.palette.primary.main
  },
  [theme.breakpoints.down("sm")]: {
    width: "95%"
  }
}));

const FloatingAcceptButton = styled(Fab)(({ theme }) => ({
  position: "fixed",
  bottom: theme.spacing(4),
  right: theme.spacing(4),
  fontWeight: 600,
  boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.6)}`
  },
  [theme.breakpoints.down("sm")]: {
    bottom: theme.spacing(3),
    right: theme.spacing(3)
  }
}));

const AgreementModal = ({
  open,
  onClose,
  onAccept,
  value: htmlValue,
  userId,
  checkNotesReminder,
  title = "Agreement"
}) => {
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [showScrollGuide, setShowScrollGuide] = useState(true);
  const dispatch = useDispatch();
  const theme = useTheme();

  // Ref for the content container
  const contentRef = React.useRef(null);

  // Handle scroll event
  const handleScroll = useCallback(() => {
    if (!contentRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
    const scrolledToBottom = scrollHeight - scrollTop - clientHeight < 100;

    if (scrollTop > 100 && !hasScrolled) {
      setHasScrolled(true);
    }

    if (scrolledToBottom) {
      setShowScrollGuide(false);
    } else {
      setShowScrollGuide(true);
    }
  }, [hasScrolled]);

  // Add scroll event listener
  useEffect(() => {
    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener("scroll", handleScroll);
      return () => contentElement.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  // Get theme color - simplified version of what was in the original component
  const themeColor = {
    color: theme.palette.text.primary,
    primary: theme.palette.primary.main
  };

  const updateImageSources = useCallback(
    async (html, color) => {
      setLoading(true);
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const images = Array.from(doc.querySelectorAll("img"));

        for (let img of images) {
          const fileName = img.getAttribute("src");

          if (fileName && !fileName.startsWith("http")) {
            try {
              const signedUrl = await generateSignedUrl(
                fileName,
                process.env.REACT_APP_BACKEND_TRAINING_IMAGE_LOCATION
              );
              img.src = signedUrl;
            } catch (error) {
              console.error("Error generating signed URL:", error);
            }
          }
          if (!img.hasAttribute("width")) {
            img.setAttribute("width", "100%");
          }
          if (!img.hasAttribute("height")) {
            img.setAttribute("height", "100%");
          }
        }

        const links = Array.from(doc.querySelectorAll("a"));
        links.forEach((link) => {
          link.target = "_blank";
          let href = link.getAttribute("href");
          link.style.background = "";
          link.style.color = "";
          if (href) {
            if (href.startsWith("www.")) {
              link.setAttribute("href", `https://${href}`);
            } else if (!isValidUrl(href)) {
              link.setAttribute("href", `https://${href}`);
            }
          }
        });

        const paragraphs = [
          ...Array.from(doc.querySelectorAll("span")),
          ...Array.from(doc.querySelectorAll("strong")),
          ...Array.from(
            doc.querySelectorAll(["h1", "h2", "h3", "h4", "h5", "h6"])
          )
        ];
        paragraphs.forEach((p) => {
          p.style.background = "";
          p.style.color = color;
        });

        const allParas = Array.from(doc.querySelectorAll("p"));
        let consecutiveJustifyBrs = [];

        allParas.forEach((p) => {
          if (
            p.classList.contains("ql-align-justify") &&
            p.innerHTML.trim() === "<br>"
          ) {
            consecutiveJustifyBrs.push(p);
          } else {
            if (consecutiveJustifyBrs.length > 7) {
              const lastFourElements = consecutiveJustifyBrs.slice(-4);
              lastFourElements.forEach((element) => element.remove());
            }
            consecutiveJustifyBrs = [];
          }
          p.style.color = color;
        });

        if (consecutiveJustifyBrs.length > 7) {
          const lastFourElements = consecutiveJustifyBrs.slice(-4);
          lastFourElements.forEach((element) => element.remove());
        }

        setValue(doc.body.innerHTML);
      } catch (err) {
        console.error("Error processing HTML:", err);
        setValue(html || "<p>No content available.</p>");
      } finally {
        setLoading(false);
      }
    },
    [setValue]
  );

  useEffect(() => {
    const initialHtml =
      htmlValue || "<p style='text-align: center;'>Loading content...</p>";
    updateImageSources(initialHtml, themeColor.color);
  }, [updateImageSources, htmlValue, themeColor.color]);

  const sanitizedContent = useSanitizeContent(value);

  return (
    <StyledModal
      open={open}
      onClose={onClose}
      disableAutoFocus
      disableEnforceFocus
      autoFocus={false}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500
      }}
      aria-labelledby="agreement-modal-title"
    >
      <Fade in={open}>
        <ModalContent ref={contentRef} elevation={24}>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="300px"
            >
              <CircularProgress color="primary" />
            </Box>
          ) : (
            <>
              <Box
                className="agreement-content"
                sx={{
                  padding: "30px",
                  mt: 0,
                  ".ql-align-center": {
                    display: "flex",
                    justifyContent: "center"
                  },
                  p: {
                    color: `${themeColor.color} !important`,
                    fontSize: "1rem",
                    lineHeight: 1.8,
                    mb: 2
                  },
                  "h1, h2, h3": {
                    color: `${themeColor.primary} !important`,
                    mb: 2,
                    mt: 4,
                    fontWeight: 600
                  },
                  "h4, h5, h6": {
                    color: alpha(themeColor.primary, 0.9),
                    mb: 2,
                    fontWeight: 500
                  },
                  img: {
                    maxWidth: "100%",
                    height: "auto",
                    borderRadius: 2,
                    my: 3,
                    boxShadow: `0 4px 8px ${alpha(
                      theme.palette.common.black,
                      0.1
                    )}`
                  },
                  a: {
                    color: "primary.main",
                    textDecoration: "none",
                    fontWeight: 500,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      textDecoration: "underline",
                      opacity: 0.9
                    }
                  },
                  "ul, ol": {
                    pl: 3,
                    mb: 3,
                    li: {
                      mb: 1
                    }
                  },
                  blockquote: {
                    borderLeft: `4px solid ${alpha(
                      theme.palette.primary.main,
                      0.5
                    )}`,
                    pl: 2,
                    py: 1,
                    my: 3,
                    fontStyle: "italic",
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    borderRadius: 1
                  }
                }}
                dangerouslySetInnerHTML={{
                  __html: sanitizedContent
                }}
              />
              <FloatingAcceptButton
                size="large"
                color="primary"
                onClick={onAccept}
                disabled={loading}
                variant="extended"
                sx={{
                  px: 3,
                  zIndex: 1300,
                  margin: "0 auto",
                  marginBottom: "20px",
                  display: "flex",
                  justifyContent: "center"
                }}
              >
                <CheckIcon sx={{ mr: 1 }} />I Accept
              </FloatingAcceptButton>
            </>
          )}
        </ModalContent>
      </Fade>
    </StyledModal>
  );
};

export default AgreementModal;
